import { zColor } from '@remotion/zod-types';
import { z } from 'zod';

const baseVisualizerSchema = z.object({
  numberOfSamples: z.enum(['32', '64', '128', '256', '512']),
});

const spectrumVisualizerSchema = baseVisualizerSchema.extend({
  type: z.literal('spectrum'),
  linesToDisplay: z.number().int().min(0).default(65),
  freqRangeStartIndex: z.number().int().min(0).default(0),
  mirrorWave: z.boolean(),
});

const oscilloscopeVisualizerSchema = baseVisualizerSchema.extend({
  type: z.literal('oscilloscope'),
  windowInSeconds: z.number().min(0.1).default(0.1),
  posterization: z.number().int().min(0.1).default(3),
  amplitude: z.number().int().min(0.1).default(4),
  padding: z.number().int().min(0).default(50),
});

const visualizerSchema = z.discriminatedUnion('type', [
  spectrumVisualizerSchema,
  oscilloscopeVisualizerSchema,
]);

export type VisualizerSchemaType = z.infer<typeof visualizerSchema>;

export const audiogramSchema = z.object({
  variant: z.enum(['1x1', '16x9', '9x16']).default('1x1'),

  // visualizer settings
  visualizer: visualizerSchema,

  // ui data
  coverImageUrl: z.string(),
  titleText: z.string(),
  titleColor: zColor(),
  visualizerColor: zColor(),

  // background settings
  backgroundType: z.enum(['solid', 'image']).default('solid'),
  backgroundImageUrl: z.string().optional(),
  backgroundColor: zColor().optional(),

  // captions settings
  captionsUrl: z.string().optional(),
  captions: z
    .array(
      z.object({
        text: z.string(),
        startMs: z.number(),
        endMs: z.number(),
        timestampMs: z.number().nullable(),
        confidence: z.number().nullable(),
      }),
    )
    .optional(),
  captionsTextColor: zColor(),
  onlyDisplayCurrentSentence: z.boolean(),
  captionsType: z.string().default('default'),

  // audio settings
  audioFileUrl: z.string(),
  audioOffsetInSeconds: z.number().min(0),
});

export type AudiogramProps = z.infer<typeof audiogramSchema>;
